import React, { type FunctionComponent, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import { Button, Modal } from '@retailer-platform/shared-components'
import { AislesSelect } from 'gin-and-tonic/containers/aisles-select/AislesSelect'
import { DepartmentsSelect } from 'gin-and-tonic/containers/departments-select/DepartmentsSelect'

interface EditProductsModalProps {
  /**
   * The current Retailer's ID. Will display Departments and Aisles within this Retailer.
   */
  retailerId: string

  /**
   * The number of products that have been selected
   */
  productCount: number

  /**
   * Notifies when the save button is pressed, with the newly selected departmentId and aisleId
   */
  onSave?: (departmentId: string, aisleId: string) => void

  /**
   * Notifies when the close button is pressed
   */
  onClose?: () => void
}

const Container = styled.div({
  display: 'flex',
  flex: 1,
  height: '80%',
  minHeight: '100px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})

const ExistingAisleNoteContainer = styled.p({
  marginTop: '20px',
  fontSize: 'small',
})

export const EditProductsModal: FunctionComponent<EditProductsModalProps> = ({
  retailerId,
  productCount,
  onSave,
  onClose,
}) => {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(null)
  const [selectedAisleId, setSelectedAisleId] = useState<string>(null)

  const header = (
    <FormattedMessage id="productsDomain.search.edit.title" values={{ count: productCount }} />
  )
  const body = (
    <Container>
      <React.Fragment>
        <DepartmentsSelect
          retailerId={retailerId}
          onChange={newVal => setSelectedDepartmentId(newVal)}
        />
        <br />
        <AislesSelect
          departmentId={selectedDepartmentId}
          onChange={newVal => setSelectedAisleId(newVal)}
        />
        <ExistingAisleNoteContainer>
          <FormattedMessage id="productsDomain.search.edit.existingAislesNote" />
        </ExistingAisleNoteContainer>
      </React.Fragment>
    </Container>
  )
  const footer = (
    <Button
      disabled={!selectedAisleId || !selectedDepartmentId}
      onClick={() => onSave?.(selectedDepartmentId, selectedAisleId)}
      aria-label={'save-button'}
    >
      <FormattedMessage id="action.save" />
    </Button>
  )

  return (
    <Modal
      onRequestClose={onClose}
      isOpen={true}
      headerContent={header}
      bodyContent={body}
      footerContent={footer}
    />
  )
}
